/**
 * GoalsWidget - View and manage goals with linked habits, todos, and projects
 */

import React, { useState } from 'react';
import { Plus, Target, Check, Pause, Archive, Pencil, Trash2, Loader2 } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import { AdaptiveModal } from '../AdaptiveModal';
import type { GoalsWidgetConfig } from '../../types/widgets';
import type { Goal, GoalStatus } from '../../types';
import { GoalStatus as GoalStatusEnum } from '../../types';
import { useGoals } from '../../hooks/useGoals';
import { GoalDetailModal } from '../GoalDetailModal';

interface GoalsWidgetProps {
  config: GoalsWidgetConfig;
  widgetId: string;
  windowId: string;
}

const STATUS_CONFIG: Record<GoalStatus, { label: string; color: string; icon: React.ElementType }> = {
  active: { label: 'Active', color: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/40', icon: Target },
  achieved: { label: 'Achieved', color: 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/40', icon: Check },
  paused: { label: 'Paused', color: 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/40', icon: Pause },
  abandoned: { label: 'Abandoned', color: 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-gray-800', icon: Archive },
};

interface GoalFormData {
  name: string;
  description: string;
  target_date: string;
}

function GoalForm({
  initial,
  onSubmit,
  onCancel,
  onDelete,
  submitLabel,
}: {
  initial?: GoalFormData;
  onSubmit: (data: GoalFormData) => void;
  onCancel: () => void;
  onDelete?: () => void;
  submitLabel: string;
}) {
  const [form, setForm] = useState<GoalFormData>(
    initial || { name: '', description: '', target_date: '' }
  );
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-2">
      <input
        type="text"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Goal name..."
        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        autoFocus
      />
      <textarea
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="Description (optional)..."
        rows={2}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none"
      />
      <input
        type="date"
        value={form.target_date}
        onChange={(e) => setForm({ ...form, target_date: e.target.value })}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
      />
      <div className="flex gap-2">
        <button
          onClick={() => form.name.trim() && onSubmit(form)}
          disabled={!form.name.trim()}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
        >
          Cancel
        </button>
        {onDelete && (
          <button
            onClick={() => {
              if (confirmDelete) onDelete();
              else setConfirmDelete(true);
            }}
            className={`ml-auto px-2 py-1 text-sm rounded ${
              confirmDelete
                ? 'bg-red-600 text-white'
                : 'text-red-500 hover:text-red-700 dark:hover:text-red-400'
            }`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function GoalRow({
  goal,
  onClick,
  onEdit,
  onStatusChange,
}: {
  goal: Goal;
  onClick: () => void;
  onEdit: () => void;
  onStatusChange: (status: GoalStatus) => void;
}) {
  const config = STATUS_CONFIG[goal.status];
  const StatusIcon = config.icon;

  return (
    <div className="group">
      <div
        className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700/50 cursor-pointer"
        onClick={onClick}
      >
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${config.color}`}>
          <StatusIcon className="w-3 h-3" />
        </span>
        <span className="flex-1 text-sm text-gray-800 dark:text-gray-200 truncate">{goal.name}</span>
        {goal.target_date && (
          <span className="text-xs text-gray-400">
            {new Date(goal.target_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          className="p-1 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-gray-600 dark:hover:text-gray-300 transition-opacity"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <select
          value={goal.status}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onStatusChange(e.target.value as GoalStatus)}
          className="text-xs bg-transparent border-none text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
        >
          <option value="active">Active</option>
          <option value="achieved">Achieved</option>
          <option value="paused">Paused</option>
          <option value="abandoned">Abandoned</option>
        </select>
      </div>
      {goal.description && (
        <p className="text-xs text-gray-500 dark:text-gray-400 pl-8 pb-1 truncate">{goal.description}</p>
      )}
    </div>
  );
}

export function GoalsWidget({ config, widgetId, windowId }: GoalsWidgetProps) {
  const showAchieved = config.showAchieved ?? false;

  const { goals, isLoading, createGoal, updateGoal, deleteGoal } = useGoals();
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const filteredGoals = showAchieved
    ? goals
    : goals.filter((g) => g.status === GoalStatusEnum.ACTIVE || g.status === GoalStatusEnum.PAUSED);

  const handleCreate = async (data: GoalFormData) => {
    await createGoal({
      name: data.name,
      description: data.description || undefined,
      target_date: data.target_date || undefined,
    });
    setShowAdd(false);
  };

  const handleUpdate = async (goalId: string, data: GoalFormData) => {
    await updateGoal({
      goalId,
      update: {
        name: data.name,
        description: data.description || undefined,
        target_date: data.target_date || undefined,
      },
    });
    setEditingId(null);
  };

  const handleStatusChange = async (goalId: string, status: GoalStatus) => {
    await updateGoal({ goalId, update: { status } });
  };

  const handleDelete = async (goalId: string) => {
    await deleteGoal(goalId);
    setEditingId(null);
  };

  const goalsContent = (
    <div className="flex flex-col h-full">
      {/* Add button */}
      <div className="flex justify-end px-2 pb-2">
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Goal
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="px-2 pb-2">
          <GoalForm
            onSubmit={handleCreate}
            onCancel={() => setShowAdd(false)}
            submitLabel="Create"
          />
        </div>
      )}

      {/* Goal list */}
      <div className="flex-1 overflow-auto px-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : filteredGoals.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">
            No goals yet. Add one to get started!
          </p>
        ) : (
          filteredGoals.map((goal) =>
            editingId === goal.id ? (
              <div key={goal.id} className="px-1 pb-2">
                <GoalForm
                  initial={{
                    name: goal.name,
                    description: goal.description || '',
                    target_date: goal.target_date ? goal.target_date.split('T')[0] : '',
                  }}
                  onSubmit={(data) => handleUpdate(goal.id, data)}
                  onCancel={() => setEditingId(null)}
                  onDelete={() => handleDelete(goal.id)}
                  submitLabel="Save"
                />
              </div>
            ) : (
              <GoalRow
                key={goal.id}
                goal={goal}
                onClick={() => setSelectedGoalId(goal.id)}
                onEdit={() => setEditingId(goal.id)}
                onStatusChange={(status) => handleStatusChange(goal.id, status)}
              />
            )
          )
        )}
      </div>

      {/* Status counts */}
      {!isLoading && goals.length > 0 && (
        <div className="flex gap-3 px-3 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
          <span>{goals.filter((g) => g.status === 'active').length} active</span>
          <span>{goals.filter((g) => g.status === 'achieved').length} achieved</span>
        </div>
      )}
    </div>
  );

  return (
    <>
      <WidgetWrapper
        title={config.title || 'Goals'}
        widgetId={widgetId}
        windowId={windowId}
        onHeaderClick={() => setExpanded(true)}
      >
        {goalsContent}
      </WidgetWrapper>

      {expanded && (
        <AdaptiveModal isOpen onClose={() => setExpanded(false)} title={config.title || 'Goals'} size="xl">
          <div className="max-h-[80vh] overflow-auto">{goalsContent}</div>
        </AdaptiveModal>
      )}

      {/* Goal detail modal */}
      {selectedGoalId && (
        <GoalDetailModal
          goalId={selectedGoalId}
          isOpen={!!selectedGoalId}
          onClose={() => setSelectedGoalId(null)}
        />
      )}
    </>
  );
}
